import {useState} from 'react';
import {trpc} from '@common-web/lib/trpc';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Plus, Trash2, Loader2, AlertCircle, Search, UserPlus} from 'lucide-react';

interface StepActionSetCommissionAgentsProps {
  onboardingId: string;
  stepId: string;
  clientId: string;
  groupId: string | null;
  onComplete: () => void;
}

export function StepActionSetCommissionAgents({
  onboardingId,
  stepId,
  clientId,
  groupId,
  onComplete,
}: StepActionSetCommissionAgentsProps) {
  const [error, setError] = useState<string | null>(null);
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);
  const [agentSearch, setAgentSearch] = useState('');
  const [commissionRate, setCommissionRate] = useState('0');
  const [commissionRateES, setCommissionRateES] = useState('');
  const [commissionRateEC, setCommissionRateEC] = useState('');
  const [commissionRateFA, setCommissionRateFA] = useState('');

  const utils = trpc.useUtils();

  const groupPlansQuery = trpc.vba.groups.groupPlans.listGroupPlanDetails.useQuery(
    {groupId: groupId ?? ''},
    {enabled: !!groupId},
  );

  const searchAgentsQuery = trpc.vba.groups.groupPlans.searchAvailableAgents.useQuery(
    {planDetailId: expandedPlanId ?? '', search: agentSearch || undefined},
    {enabled: !!expandedPlanId},
  );

  const addAgentMutation = trpc.vba.groups.groupPlans.addAgentToGroupPlanDetail.useMutation({
    onSuccess: () => {
      if (groupId) {
        utils.vba.groups.groupPlans.listGroupPlanDetails.invalidate({groupId});
      }
      if (expandedPlanId) {
        utils.vba.groups.groupPlans.searchAvailableAgents.invalidate({
          planDetailId: expandedPlanId,
        });
      }
      setError(null);
      setCommissionRate('0');
      setCommissionRateES('');
      setCommissionRateEC('');
      setCommissionRateFA('');
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const removeAgentMutation = trpc.vba.groups.groupPlans.removeAgentFromGroupPlanDetail.useMutation(
    {
      onSuccess: () => {
        if (groupId) {
          utils.vba.groups.groupPlans.listGroupPlanDetails.invalidate({groupId});
        }
      },
    },
  );

  const updateRateMutation = trpc.vba.groups.groupPlans.updateGroupPlanAgentRate.useMutation({
    onSuccess: () => {
      if (groupId) {
        utils.vba.groups.groupPlans.listGroupPlanDetails.invalidate({groupId});
      }
    },
  });

  const completeStepMutation = trpc.internal.onboarding.completeStep.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.getOnboarding.invalidate({onboardingId});
      onComplete();
    },
  });

  function handleAddAgent(agentId: string) {
    if (!expandedPlanId) return;
    addAgentMutation.mutate({
      planDetailId: expandedPlanId,
      agentId,
      commissionRate: parseFloat(commissionRate) || 0,
      commissionRateES: commissionRateES ? parseFloat(commissionRateES) : null,
      commissionRateEC: commissionRateEC ? parseFloat(commissionRateEC) : null,
      commissionRateFA: commissionRateFA ? parseFloat(commissionRateFA) : null,
    });
  }

  function handleRemoveAgent(planDetailId: string, agentId: string, payrollCycleId: string | null) {
    removeAgentMutation.mutate({planDetailId, agentId, payrollCycleId});
  }

  function handleMarkComplete() {
    completeStepMutation.mutate({stepId});
  }

  if (!groupId) {
    return (
      <div className='rounded-md bg-amber-50 p-3'>
        <p className='text-xs text-amber-700'>
          A group must be created and plans must be assigned before commission agents can be set.
        </p>
      </div>
    );
  }

  const plans = groupPlansQuery.data?.plans ?? [];

  if (plans.length === 0) {
    return (
      <div className='rounded-md bg-amber-50 p-3'>
        <p className='text-xs text-amber-700'>
          No plans have been assigned to this group yet. Complete the "Setup Plans" step first.
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='rounded-md bg-blue-50 p-3'>
        <p className='text-xs text-blue-700'>
          Assign commission agents to each plan for this group. Click a plan to expand and manage
          agents.
        </p>
      </div>

      {plans.map(
        (plan: {
          id: string;
          groupPlanConfigurationVariation: {
            name: string;
            groupPlanConfiguration: {name: string; includesFamilyCoverage: boolean};
          };
          commissionAgentPlans: Array<{
            commissionAgentId: string;
            commissionRate: unknown;
            commissionRateES: unknown;
            commissionRateEC: unknown;
            commissionRateFA: unknown;
            commissionAgent: {
              id: string;
              name: string;
              admin123AgentId: string | null;
            };
          }>;
          defaultAgentCommission: unknown;
        }) => {
          const hasFamilyCoverage =
            plan.groupPlanConfigurationVariation.groupPlanConfiguration.includesFamilyCoverage;
          const isExpanded = expandedPlanId === plan.id;

          return (
            <Card key={plan.id}>
              <CardContent className='p-3'>
                <div
                  className='flex items-center justify-between cursor-pointer'
                  onClick={() => setExpandedPlanId(isExpanded ? null : plan.id)}
                >
                  <div>
                    <p className='text-sm font-medium text-gray-900'>
                      {plan.groupPlanConfigurationVariation.groupPlanConfiguration.name}
                    </p>
                    <p className='text-xs text-gray-500'>
                      {plan.groupPlanConfigurationVariation.name}
                    </p>
                  </div>
                  <Badge variant='outline' className='text-xs'>
                    {plan.commissionAgentPlans.length} agent
                    {plan.commissionAgentPlans.length !== 1 ? 's' : ''}
                  </Badge>
                </div>

                {/* Assigned agents */}
                {plan.commissionAgentPlans.length > 0 && (
                  <div className='mt-2 space-y-1'>
                    {plan.commissionAgentPlans.map((ap) => (
                      <div
                        key={ap.commissionAgentId}
                        className='flex items-center justify-between py-1.5 px-2 rounded-sm bg-gray-50'
                      >
                        <div>
                          <span className='text-xs font-medium text-gray-700'>
                            {ap.commissionAgent.name}
                          </span>
                          {ap.commissionAgent.admin123AgentId && (
                            <span className='text-xs text-gray-400 ml-1'>
                              ({ap.commissionAgent.admin123AgentId})
                            </span>
                          )}
                        </div>
                        <div className='flex items-center gap-2'>
                          <span className='text-xs text-gray-500'>
                            ${String(ap.commissionRate)}
                            {ap.commissionRateES != null && ` ES:$${String(ap.commissionRateES)}`}
                            {ap.commissionRateEC != null && ` EC:$${String(ap.commissionRateEC)}`}
                            {ap.commissionRateFA != null && ` FA:$${String(ap.commissionRateFA)}`}
                            {ap.payrollCycleId && ` (${ap.payrollCycleId})`}
                          </span>
                          <Button
                            variant='ghost'
                            size='icon'
                            className='h-6 w-6'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveAgent(
                                plan.id,
                                ap.commissionAgentId,
                                ap.payrollCycleId ?? null,
                              );
                            }}
                            disabled={removeAgentMutation.isPending}
                          >
                            <Trash2 className='h-3 w-3 text-red-500' />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Agent search panel */}
                {isExpanded && (
                  <div className='mt-3 pt-3 border-t space-y-2'>
                    <div className='flex items-center gap-2'>
                      <div className='relative flex-1'>
                        <Search className='absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400' />
                        <Input
                          className='pl-7 h-8 text-xs'
                          placeholder='Search agents...'
                          value={agentSearch}
                          onChange={(e) => setAgentSearch(e.target.value)}
                        />
                      </div>
                      <div className='flex items-center gap-1'>
                        <Label className='text-xs whitespace-nowrap'>EM Rate $</Label>
                        <Input
                          className='h-8 w-20 text-xs'
                          type='number'
                          step='0.01'
                          min='0'
                          value={commissionRate}
                          onChange={(e) => setCommissionRate(e.target.value)}
                        />
                      </div>
                    </div>
                    {hasFamilyCoverage && (
                      <div className='flex items-center gap-2'>
                        <div className='flex items-center gap-1'>
                          <Label className='text-xs whitespace-nowrap'>ES $</Label>
                          <Input
                            className='h-8 w-16 text-xs'
                            type='number'
                            step='0.01'
                            min='0'
                            placeholder='=EM'
                            value={commissionRateES}
                            onChange={(e) => setCommissionRateES(e.target.value)}
                          />
                        </div>
                        <div className='flex items-center gap-1'>
                          <Label className='text-xs whitespace-nowrap'>EC $</Label>
                          <Input
                            className='h-8 w-16 text-xs'
                            type='number'
                            step='0.01'
                            min='0'
                            placeholder='=EM'
                            value={commissionRateEC}
                            onChange={(e) => setCommissionRateEC(e.target.value)}
                          />
                        </div>
                        <div className='flex items-center gap-1'>
                          <Label className='text-xs whitespace-nowrap'>FA $</Label>
                          <Input
                            className='h-8 w-16 text-xs'
                            type='number'
                            step='0.01'
                            min='0'
                            placeholder='=EM'
                            value={commissionRateFA}
                            onChange={(e) => setCommissionRateFA(e.target.value)}
                          />
                        </div>
                      </div>
                    )}

                    {searchAgentsQuery.isLoading ? (
                      <div className='text-center py-2'>
                        <Loader2 className='h-4 w-4 animate-spin mx-auto text-gray-400' />
                      </div>
                    ) : (searchAgentsQuery.data ?? []).length === 0 ? (
                      <p className='text-xs text-gray-400 text-center py-2'>No agents found.</p>
                    ) : (
                      <div className='max-h-40 overflow-y-auto space-y-1'>
                        {(searchAgentsQuery.data ?? []).map(
                          (agent: {
                            id: string;
                            name: string;
                            admin123AgentId: string | null;
                            defaultCommissionRate: unknown;
                          }) => (
                            <div
                              key={agent.id}
                              className='flex items-center justify-between py-1.5 px-2 rounded-sm hover:bg-gray-50'
                            >
                              <div>
                                <span className='text-xs font-medium text-gray-700'>
                                  {agent.name}
                                </span>
                                {agent.admin123AgentId && (
                                  <span className='text-xs text-gray-400 ml-1'>
                                    ({agent.admin123AgentId})
                                  </span>
                                )}
                              </div>
                              <Button
                                variant='ghost'
                                size='sm'
                                className='h-6 text-xs'
                                onClick={() => handleAddAgent(agent.id)}
                                disabled={addAgentMutation.isPending}
                              >
                                <UserPlus className='h-3 w-3 mr-1' />
                                Add
                              </Button>
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        },
      )}

      {error && (
        <div className='rounded-md bg-red-50 p-3 flex items-start gap-2'>
          <AlertCircle className='h-4 w-4 text-red-600 shrink-0 mt-0.5' />
          <p className='text-xs text-red-700'>{error}</p>
        </div>
      )}

      <Button
        className='w-full bg-emerald-600 hover:bg-emerald-700 text-white'
        onClick={handleMarkComplete}
        disabled={completeStepMutation.isPending}
      >
        {completeStepMutation.isPending ? 'Completing...' : 'Mark Commission Agents Complete'}
      </Button>
    </div>
  );
}
